import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pressable, StyleSheet, View } from 'react-native'
import { useViewportLayout } from 'utils/helper-device'
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import { useKeepAwake } from 'expo-keep-awake';
import Text from "theme/Text";
import ThemedCamera, { cameraFacing } from 'components/atoms/ThemedCamera'
import FetchBooking from 'utils/fetch/FetchBooking'
import _ from 'lodash'
import HelperDate, { dateFormat } from 'utils/helper-date'
import { colorList } from 'utils/colors'
import { useAudioPlayer } from 'expo-audio';
import MutateScan from 'utils/mutate/scan'
import { scanThroughConstants } from 'utils/constants/scan-constants'
import SecureStoreService from 'services/store/secure-store'
import { storeKeys } from 'services/store/keys'
import { StackActions, useIsFocused, useNavigation } from '@react-navigation/native'
import { AppRoutes } from 'navigations/app-routes'
import i18n from 'utils/i18n'
import ThemedModal from 'components/atoms/ThemedModal'
import HelperVersion from 'utils/helper-version'
import HelperSetting from 'utils/helper-setting'
import { settingsConstants } from 'utils/constants/settings-constants'
import ThemedPressable from 'components/atoms/ThemedPressable'
import AppUpdater from 'services/app-updater'
import CacheStorage from 'utils/cache/CacheStorage'
import HelperFileCache from 'utils/HelperFileCache'
import HelperAlerts from 'utils/HelperAlerts'
import AccountStorage from 'utils/accounts/AccountStorage'
import UserService from 'services/user'
import store from 'services/redux/store'
import { resetData } from 'services/redux/data/actions'
import { resetManagement } from 'services/redux/management/actions'
import { resetAuthentication } from 'services/redux/authentication/actions'
import { resetCart } from 'services/redux/cart/actions'
import LunchScannerBanner from 'scenes/home/lunchScanner/components/Banner'
import LunchScannerResults from 'scenes/home/lunchScanner/components/Results'
import { useScannerFullScreen } from 'utils/hooks/use-scanner-full-screen'
import GraphqlGroupedBuilder from 'utils/request/graphql/groupedBuilder'
import QueryBuildings from 'graphql/queries/lunchScan/QueryBuildingScans'
import HelperPlatform from 'utils/helper-platform'
import IndexUpdateSceneBlock, { UPDATE_VARIANT } from 'scenes/home/home/_scene_blocks/index/update'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import ScannerControls from 'scenes/home/lunchScanner/components/Controls'
import { IconBackground } from 'components/atoms/IconBackground'

export const SCAN_STATUS = {
	PENDING: 'PENDING',
	LOADING: 'LOADING',
	VALIDATE: 'VALIDATE',
	VALID: 'VALID',
	WRONG_DAY: 'WRONG_DAY',
	ALREADY_CHECKED_IN: 'ALREADY_CHECKED_IN',
	INVALID: 'INVALID',
}

const cameraTimeout = 60*60*1000; // 60 minutes of no scanning
const confirmTimeout = 4*1000; // 5 seconds
const errorTimeout = 2*1000; // 3 seconds

const HomeLunchScannerIndexScene = () => {
	
	useKeepAwake();
	
	const isFocused = useIsFocused();
	const navigation = useNavigation();
	useScannerFullScreen();
	const successSound = useAudioPlayer( require( 'assets/audio/success.mp3' ) )
	const errorSound = useAudioPlayer( require( 'assets/audio/error.mp3' ) )
	
	const sleepTimeout = useRef( null );
	const scanPollTimeout = useRef( null );
	const scannerActive = useRef( false );
	
	const [ facing, setFacing ] = useState( cameraFacing.BACK )
	const [ qrCode, setQrCode ] = useState( null );
	const [ status, setStatus ] = useState( SCAN_STATUS.PENDING );
	const [ employeeBookingDb, setEmployeeBookingDb ] = useState( null );
	const [ updateIsChecking, setUpdateIsChecking ] = useState( false );
	const [ showActionModal, setShowActionModal ] = useState( false );
	
	const [ timeslotScans, setTimeslotScans ] = useState( [] );
	
	const viewport = useViewportLayout();
	const compact = viewport.isCompactDevice;
	const insets = useSafeAreaInsets();
	
	useEffect( () => {
		scannerActive.current = isFocused;
		if( isFocused ) {
			fetchScans();
		}
		return () => {
			scannerActive.current = false;
			clearTimeout( scanPollTimeout.current );
			clearTimeout( sleepTimeout.current );
		};
	}, [ isFocused ] );
	


	useEffect( () => {
		resetTimeout();
		
		SecureStoreService
			.get( storeKeys.CHECKIN_CAMERA_TYPE )
			.then( cameraFacing => {
				if( cameraFacing ){
					setFacing( cameraFacing )
				}
		});
	}, [] );
	
	useEffect( () => {
		if( isFocused ){
			resetTimeout();
		}
	}, [ isFocused ] );
	
	useEffect( () => {
		if( !qrCode ) {
			return;
		}
		
		resetTimeout();
		
		let data = null;
		
		try{
			data = JSON.parse( qrCode );
		}catch( e ) {
			setStatus( SCAN_STATUS.INVALID );
			return;
		}
		
		if( !data ){
			setStatus( SCAN_STATUS.INVALID );
			return;
		}
		
		const employeeBookingGuid = _.get( data, 'employeeBooking' );
		const employeeGuid = _.get( data, 'employee' );
		
		if( !employeeBookingGuid || !employeeGuid ){
			setStatus( SCAN_STATUS.INVALID );
			return;
		}
		
		FetchBooking.fetchByGuid( employeeBookingGuid, employeeGuid, {
			callbackStart: () => {
				setStatus( SCAN_STATUS.LOADING );
			},
			callbackSuccess: fetchedBooking
		});
	}, [ qrCode ] );
	
	useEffect( () => {
		switch( status ) {
			case SCAN_STATUS.VALID:
				playSound( 'success' );
				break;
				
			case SCAN_STATUS.WRONG_DAY:
			case SCAN_STATUS.ALREADY_CHECKED_IN:
			case SCAN_STATUS.INVALID:
				setTimeout( reset, errorTimeout );
				playSound( 'error' );
				break;
		}
	}, [ status ] );
	
	const fetchScans = () => {
		GraphqlGroupedBuilder.addQuery( 'buildings', QueryBuildings, {
			variables: {
				scanInAtDate: HelperDate.date().format( dateFormat.DATE ) +' 00:00:00_after',
			},
			resultPath: [ 'buildings' ],
			callbackStart: () => {
			
			},
			callbackError: result => {
			
			},
			callbackSuccess: result => {
				if( !scannerActive.current ) {
					return;
				}
				let collectedData = {};
				
				_.forEach( result, ( building ) => {
					_.forEach( building?.scans, ( scan ) => {
						const timeslot = _.get( scan, 'timeslot' );
						
						if( timeslot ) {
							if( !collectedData[ timeslot.guid ] ){
								collectedData[ timeslot.guid ] = {
									timeslot_start: timeslot.timeslot_start,
									timeslot_end: timeslot.timeslot_end,
									capacity: timeslot.capacity,
									scanned: 0,
								}
							}
							
							collectedData[ timeslot.guid ].scanned++;
						}
					});
				});
				
				setTimeslotScans( collectedData );
				
				scanPollTimeout.current = setTimeout( fetchScans, 1000*60 );
			}
		})
	}
	
	const sleep = () => {
		navigation.dispatch(
			StackActions.replace(AppRoutes.LUNCH_SLEEPING )
		);
	}
	
	const resetTimeout = () => {
		clearTimeout( sleepTimeout.current );
		sleepTimeout.current = setTimeout( () => {
			sleep();
		}, cameraTimeout );
	}
	
	const fetchedBooking = ( bookings ) => {
		
		const booking = _.get( bookings, [ '0' ], null );
		
		if( !booking ) {
			setStatus( SCAN_STATUS.INVALID );
			return;
		}
		
		if( HelperDate.date().format( dateFormat.DATE ) !== _.get( booking, 'booking_date' ) ){
			setStatus( SCAN_STATUS.WRONG_DAY );
			return;
		}
		
		let alreadyScanned = false;
		_.forEach( _.get( booking, 'employee_scans' ), ( scan ) => {
			if( _.get( scan, [ 'timeslot', 'guid' ]  ) === _.get( booking, [ 'timeslot', 'guid' ] ) ){
				alreadyScanned = true;
				return false;
			}
		});
		
		if( alreadyScanned ){
			setStatus( SCAN_STATUS.ALREADY_CHECKED_IN );
			return;
		}
		
		setEmployeeBookingDb( booking );
		setStatus( SCAN_STATUS.VALIDATE );
	}
	
	const checkIn = () => {
		const timeslotGuid = _.get( employeeBookingDb, [ 'timeslot', 'guid' ], null );
		MutateScan.scanIn( `building:${_.get( employeeBookingDb, [ 'building', 'guid' ] ) }`, { employeeGuid: _.get( employeeBookingDb, [ 'employee', 'guid' ] ), timeslotGuid: timeslotGuid, scannedInThrough: scanThroughConstants.QRCODE_BY_EMPLOYEE, callbackSuccess: checkedInEmployee, callbackStart: () => {
			setStatus(SCAN_STATUS.LOADING);
		} })
	}
	
	const checkedInEmployee = () => {
		setStatus( SCAN_STATUS.VALID );
		setTimeout( reset, confirmTimeout );
	}
	
	const reset = () => {
		setQrCode( null );
		setStatus( SCAN_STATUS.PENDING );
		setEmployeeBookingDb( null );
	}
	
	const playSound = async ( type ) => {
		let currentSound = null;
		
		try {
			switch ( type ) {
				case 'success':
					currentSound = successSound;
					break;
					
				case 'error':
					currentSound = errorSound;
					break;
			}
			
			if( currentSound !== null ) {
				await currentSound.seekTo( 0 );
				currentSound.play();
			}
		} catch ( error ) {
			console.log( 'Error playing sound:', error );
		}
	}
	
	const handleScan = ({ type, data }, callback ) => {
		callback();
		
		if( status !== SCAN_STATUS.PENDING ){
			return;
		}
		
		setQrCode( data );
	}
	
	const timeslot = useMemo( () => {
		if( employeeBookingDb ) {
			return employeeBookingDb.timeslot ?? {}
		}
		
		return {}
	}, [ employeeBookingDb ] );
	
	const building = useMemo( () => {
		if( employeeBookingDb ) {
			return employeeBookingDb.building ?? { name: '-' }
		}
		
		return {
			name: '-'
		}
	}, [ employeeBookingDb ] );
	
	const timeslotKind = {
		kind: _.get( timeslot, 'kind' ),
		sub_kind: _.get( timeslot, 'sub_kind' )
	};
	
	const toggleCameraFacing = () => {
		
		const newCameraFacing = facing === cameraFacing.BACK ? cameraFacing.FRONT : cameraFacing.BACK;
		setFacing( newCameraFacing );
		
		SecureStoreService.set( storeKeys.CHECKIN_CAMERA_TYPE, newCameraFacing );
	}
	
	const checkForUpdates = () => {
		setUpdateIsChecking( false );
		const appUpdater = new AppUpdater();
		
		appUpdater
			.check()
			.then( () => {
				if( appUpdater.updateIsAvailable ) {
					CacheStorage.clearCache( { restart: false } );
					HelperFileCache.drop();
					
					appUpdater.createAlert({
						callbackStart: setUpdateIsChecking.bind( this, true ),
						callbackSuccess: setUpdateIsChecking.bind( this, false ),
						callbackError: setUpdateIsChecking.bind( this, false )
					});
				} else {
					HelperAlerts.alert(
						i18n.t('update.up_to_date.title'),
						i18n.t('update.up_to_date.message'),
					);
					
					setUpdateIsChecking( false );
				}
			});
	}
	
	const confirmLogout = () => {
		HelperAlerts.alert(
			i18n.t( 'alerts.are_you_sure.title' ),
			null,
			[
				{
					text: i18n.t( 'buttons.cancel' ),
				},
				{
					text: i18n.t( 'buttons.logout' ),
					onPress: logout.bind( this )
				}
			]
		);
	}
	
	const logout = () => {
		AccountStorage.clearAccounts().then( () => {
			UserService.logout({
				restart: true,
				resetData: () => {
					return store.dispatch(resetData());
				},
				resetManagement: () => {
					return store.dispatch(resetManagement());
				},
				resetAuthentication: () => {
					return store.dispatch(resetAuthentication());
				},
				resetCart: () => {
					return store.dispatch(resetCart());
				}
			});
		});
	}
	
	const renderedStatistics = useMemo( () => {
		const rows = [];
		
		_.forEach( timeslotScans, ( timeslot, key ) => {
			rows.push(
				<View key={ key } style={ styles.timeslotStatisticsRow}>
					<View style={styles.viewFlex2}>
						<Text style={ styles.timeslotStatisticsText }>{ `${ timeslot.timeslot_start.substr(0, 5)}-${ timeslot.timeslot_end.substr(0, 5)}` }</Text>
					</View>
					<View style={ styles.cameraContainer}>
						<Text style={ styles.timeslotStatisticsText }>{ `${timeslot.scanned}/${timeslot.capacity}` }</Text>
					</View>
				</View>
			);
		});
		
		return (
			<View style={ styles.scannerStatistics }>
				{ rows }
			</View>
		);
	}, [ timeslotScans ] );
	
	return (
		<ThemedSafeAreaComponentTop
			top={ true }>
			
			<ThemedModal
				visible={ showActionModal }
				setVisible={ setShowActionModal }
				title={ HelperSetting.get( settingsConstants.COMPANY_NAME, null ) }>
				
				<Text>{ i18n.t( 'app_name' ) } { HelperVersion.get() }</Text>
				
				<ThemedPressable onPress={ sleep }>
					<Text style={ styles.modVersionUpdate}>{ i18n.t( 'ui.sleep' ) }</Text>
				</ThemedPressable>
				
				<ThemedPressable onPress={ confirmLogout }>
					<Text style={ styles.modVersionUpdate}>{ i18n.t('buttons.logout') }</Text>
				</ThemedPressable>
				
				<ThemedPressable onPress={ checkForUpdates }>
					<Text style={ styles.modVersionUpdate}>{ i18n.t('update.check') }</Text>
				</ThemedPressable>
			</ThemedModal>
			
			<IconBackground opacity={ 0.2 }>
				<View style={ styles.scannerHeader }>
					<View style={ styles.scannerBrand }>
						<LunchScannerBanner compact iconBackground={ false } setShowActionModal={ setShowActionModal } />
					</View>
					<ScannerControls
						onBack={ navigation.canGoBack() ? () => {
							navigation.goBack();
						} : undefined }
						onSwitchCamera={ toggleCameraFacing }
						onSettings={ () => {
							setShowActionModal( true );
						} }
					/>
				</View>
			</IconBackground>
			<View
				style={ [
					styles.container,
					{ flex: 1, height: 'auto',
						flexDirection: !compact || viewport.isLandscape ? 'row' : 'column' },
				] }>
				
				<View
					style={ styles.cameraContainer }>
					
					<ThemedCamera
						paused
						onBarCodeScanned={ handleScan }
						style={ styles.scan }
						facing={ facing }
					/>
					<View pointerEvents="none" style={ styles.cameraHint }>
						<Text style={ styles.cameraHintText }>{ i18n.t( 'lunch_scanner.index.camera_hint' ) }</Text>
					</View>
					
					{/* Place it over the camera */}
					<Pressable
						onLongPress={ toggleCameraFacing }
						style={styles.pressablePositionAbsoluteFlex1}>
						
						<View>
							{/*	Nothing */}
						</View>
					</Pressable>
					
				</View>
				
				<View
					style={ [ styles.resultContainer, compact && styles.compactResultContainer, { paddingBottom: insets.bottom } ] }>
					
					<LunchScannerResults
						compact={ compact }
						status={ status }
						employeeBookingDb={ employeeBookingDb }
						checkIn={ checkIn }
						reset={ reset }
					/>
					
					{ HelperPlatform.isNotWeb() && (
						<View
							style={ styles.compactUpdateContainer }>
							
							<IndexUpdateSceneBlock
								variant={ UPDATE_VARIANT.ONLY_UPDATE }
								isFocused={ isFocused }
							/>
						</View>
					)}
					
					{ renderedStatistics }
				</View>
			</View>
		</ThemedSafeAreaComponentTop>
	);
}

const styles = StyleSheet.create({
	scannerHeader: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	scannerBrand: { flex: 1, minWidth: 0 },
	scannerStatistics: { alignSelf: 'flex-end', marginHorizontal: 16, marginVertical: 8, width: 120 },
	cameraHint: {
		position: 'absolute',
		left: 16,
		right: 16,
		bottom: 16,
		padding: 12,
		borderRadius: 12,
		backgroundColor: 'rgba(0, 0, 0, 0.65)',
	},
	cameraHintText: { color: colorList.WHITE.primary, textAlign: 'center', fontSize: 16 },
	compactResultContainer: {
		flex: 1.2,
		minWidth: 0,
		minHeight: 0,
		maxWidth: '100%',
	},
	compactUpdateContainer: {
		paddingHorizontal: 12,
	},
	viewFlex2: {
		flex: 2,
	},
	pressablePositionAbsoluteFlex1: {
		position: 'absolute',
		flex: 1,
		width: '100%',
		height: '100%',
	},
	container: {
		flexDirection: 'row',
		height: '100%'
	},
	cameraContainer: {
		flex: 1,
	},
		scan: {
			flex: 1,
			width: '100%',
			height: '100%',
		},
	
	resultContainer: {
		flex: 1.5,
		minWidth: 0,
		minHeight: 0,
	},
	modVersionUpdate: {
		marginTop: 20,
		color: colorList.ORANGE.primary,
		fontWeight: '700',
		textDecorationLine: 'underline',
		marginBottom: 10
	},
	
	
	timeslotStatisticsText: {
		fontSize: 12,
		textAlign: 'right'
	},
	timeslotStatisticsRow: {
		flexDirection: 'row',
		alignItems: 'flex-end'
	},
	
});

export default HomeLunchScannerIndexScene;
